import { Link, useParams } from "react-router"
import { useQuery } from "@tanstack/react-query"

import { api } from "@/lib/api"
import { SectionCard } from "@/components/console/SectionCard"
import { MarkdownView } from "@/components/console/MarkdownView"
import { PageSkeleton, FetchError } from "@/components/console/Fallbacks"
import { timeAgo } from "@/lib/format"

export function History({ token }: { token: string }) {
  const { audience = "", id = "" } = useParams()
  const history = useQuery({
    queryKey: ["history", audience, id, token],
    queryFn: () => api.history(token, audience, id),
  })

  if (history.isPending) return <PageSkeleton />
  if (history.isError) return <FetchError error={history.error} retry={() => history.refetch()} />

  return (
    <SectionCard title="version history">
      {history.data.length === 0 ? (
        <p className="font-mono text-xs text-muted-foreground">
          No prior versions — this record hasn't been edited yet.
        </p>
      ) : (
        <ul className="space-y-1">
          {history.data.map((h) => (
            <li key={h.version}>
              <Link
                to={`/records/${audience}/${id}/history/${h.version}`}
                className="font-mono text-xs text-foreground hover:underline"
              >
                v{h.version}
              </Link>
              <span className="ml-2 text-[10px] text-muted-foreground">
                {timeAgo(h.modified_at)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  )
}

export function HistoryVersion({ token }: { token: string }) {
  const { audience = "", id = "", version = "" } = useParams()
  const record = useQuery({
    queryKey: ["history-version", audience, id, version, token],
    queryFn: () => api.historyVersion(token, audience, id, Number(version)),
  })

  if (record.isPending) return <PageSkeleton />
  if (record.isError) return <FetchError error={record.error} retry={() => record.refetch()} />

  return (
    <SectionCard title={`${record.data.title} — v${version}`}>
      <MarkdownView content={record.data.content} audience={audience} />
    </SectionCard>
  )
}
